<template>
  <div class="price-container">
    <div class="price-content">
      <div class="price-section">
        <h2 class="price-title">Valor do Imóvel</h2>
        <div class="price-value">
          <span class="currency">R$</span>
          {{ formattedPrice }}
        </div>
        <div v-if="showInstallments" class="installments">
          Entrada de R$ {{ formattedDownPayment }}
          <br>
          + Parcelas de R$ {{ formattedMonthlyPayment }}
        </div>
      </div>
      
      <v-divider vertical class="mx-4 hidden-sm-and-down"></v-divider>
      
      <div class="actions-section">
        <v-btn
          color="primary"
          size="large"
          class="action-button"
          prepend-icon="mdi-whatsapp"
          @click="openWhatsApp"
        >
          Falar com Corretor
        </v-btn>
        
        <v-btn
          variant="outlined"
          color="primary"
          size="large"
          class="action-button mt-2"
          prepend-icon="mdi-calculator"
          @click="showFinancingDialog = true"
        >
          Simular Financiamento
        </v-btn>
      </div>
    </div>

    <!-- Financing Dialog -->
    <v-dialog v-model="showFinancingDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5">
          Simulação de Financiamento
        </v-card-title>
        
        <v-card-text>
          <v-form ref="form">
            <v-text-field
              v-model="downPayment"
              label="Valor da Entrada"
              prefix="R$"
              type="number"
              :rules="[v => v >= minDownPayment || 'Entrada mínima de 20% do valor do imóvel']"
            ></v-text-field>

            <v-select
              v-model="years"
              :items="yearOptions"
              label="Prazo (anos)"
            ></v-select>

            <v-text-field
              v-model="interestRate"
              label="Taxa de Juros Anual"
              suffix="%"
              type="number"
              step="0.01"
              min="0"
              :rules="[v => v >= 0 || 'A taxa de juros deve ser positiva']"
            ></v-text-field>

            <v-alert
              type="info"
              variant="tonal"
              class="mb-4"
              density="comfortable"
            >
              <strong>Nota sobre a Taxa de Juros:</strong>
              <p class="mt-2">A taxa de juros apresentada é uma estimativa baseada em valores médios do mercado. 
              Para obter a taxa exata, consulte seu banco ou instituição financeira, pois ela pode variar de acordo com seu perfil, 
              score de crédito e condições do mercado.</p>
            </v-alert>

            <v-alert
              v-if="calculatedPayment"
              color="primary"
              class="mt-4"
            >
              Parcela Estimada: R$ {{ formatCurrency(calculatedPayment) }}
            </v-alert>
          </v-form>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              variant="text"
              @click="calculatePayment"
            >
              Calcular
            </v-btn>
            <v-btn
              color="grey"
              variant="text"
              @click="showFinancingDialog = false"
            >
              Fechar
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'PropertyPriceComponent',

  props: {
    price: {
      type: Number,
      required: true
    },
    whatsappNumber: {
      type: String,
      required: true
    },
    defaultInterestRate: {
      type: Number,
      default: 8.99
    }
  },

  data() {
    return {
      showFinancingDialog: false,
      showInstallments: false,
      downPayment: 0,
      years: 30,
      interestRate: this.defaultInterestRate,
      calculatedPayment: null,
      yearOptions: [
        { title: '15 anos', value: 15 },
        { title: '20 anos', value: 20 },
        { title: '25 anos', value: 25 },
        { title: '30 anos', value: 30 }
      ]
    }
  },

  computed: {
    formattedPrice() {
      return this.formatCurrency(this.price)
    },

    minDownPayment() {
      return this.price * 0.2 // 20% minimum down payment
    },

    formattedDownPayment() {
      return this.formatCurrency(this.downPayment)
    },

    formattedMonthlyPayment() {
      return this.formatCurrency(this.calculatedPayment)
    }
  },

  mounted() {
    this.downPayment = this.minDownPayment
  },

  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },

    calculatePayment() {
      if (!this.$refs.form.validate()) return

      const principal = this.price - this.downPayment
      const monthlyRate = (this.interestRate / 100) / 12
      const numberOfPayments = this.years * 12

      const payment = principal * 
        (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1)

      this.calculatedPayment = payment
      this.showInstallments = true
    },

    openWhatsApp() {
      // Rastreia a conversão do Google Ads
      this.$gtag.event('conversion', {
        'send_to': 'AW-1002935987/whatsapp_click',
        'value': this.price,
        'currency': 'BRL'
      })

      // Abre o WhatsApp
      const message = `Olá! Gostaria de mais informações sobre o imóvel anunciado por R$ ${this.formattedPrice}.`
      const whatsappUrl = `https://wa.me/${this.whatsappNumber}?text=${encodeURIComponent(message)}`
      window.open(whatsappUrl, '_blank')
    }
  }
}
</script>

<style scoped>
.price-container {
  width: 100%;
  margin: 2rem auto;
  padding: 2rem 1rem;
  max-width: 1400px;
  background-color: var(--v-surface-base);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.price-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.price-section {
  flex: 1;
}

.price-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin-bottom: 0.5rem;
  letter-spacing: -0.5px;
}

.price-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--v-secondary-base);
  letter-spacing: -1px;
  line-height: 1.2;
}

.currency {
  font-size: 1.5rem;
  font-weight: 600;
  margin-right: 0.5rem;
}

.installments {
  margin-top: 0.5rem;
  font-size: 1.1rem;
  color: var(--v-secondary-base);
  opacity: 0.9;
}

.actions-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 240px;
}

.action-button {
  width: 100%;
}

@media (max-width: 600px) {
  .price-container {
    margin: 1rem auto;
    padding: 1rem;
  }

  .price-content {
    flex-direction: column;
    text-align: center;
  }

  .price-section {
    margin-bottom: 1.5rem;
  }

  .price-value {
    font-size: 2rem;
  }

  .actions-section {
    width: 100%;
    align-items: stretch;
  }

  .currency {
    font-size: 1.25rem;
  }

  .installments {
    font-size: 1rem;
  }
}
</style>
